import { Controller } from "@hotwired/stimulus"
import { humanFileSize, fileSizeToBytes } from "../utils/files";
import Uploader from "../utils/uploader";

export default class extends Controller {
  static targets = ["input", "cachedFile", "notice", "downlading"];
  static values = { url: String };

  startUpload() {
    this.#emptyErrors();
    this.dispatch("started");
    const validFiles = this.#validateFiles(this.inputTarget.files);
    this.inputTarget.value = "";

    this.#displayErrors();

    this.#displayUploading();
    validFiles.forEach((file) => {
      this.#updateUploadProgress({ file: file, progress: 0 });
    });
    // This way we start all upload at the same time
    Promise.all( validFiles.map((file) => this.#uploadFile(file)))
      .then((files) => {
        this.#hideUploading();
        this.dispatch("finished")
      })
      .catch((error) => {
        this.#hideUploading();
        this.dispatch("finished")
      });
  }

  removeUploadedFile({ detail: { fileName } }) {
    this.#findUploadedFileByName(fileName).forEach((file) => {
      file.remove();
    })
  }

  #findUploadedFileByName(fileName) {
    return this.cachedFileTargets.filter((file) => {
      const fileNameFromInput = file.dataset.directUploadName;
      return fileNameFromInput === fileName;
    });
  }


  #uploadFile(file) {
    return new Promise((resolve, reject) => {
      new Uploader({
        file: file,
        url: this.urlValue,
        onError: (error) => {
          this.notices[file.name] ||= [];
          this.notices[file.name].push(`Error: ${error}`);
          this.#displayErrors();
          reject(error);
        },
        onProgress: (event) => {
          const progress = ((event.loaded / event.total) * 100).toFixed(1);
          this.#updateUploadProgress({ file: file, progress: progress });
        },
        onSuccess: (blob) => {
          const hiddenField = document.createElement("input");
          hiddenField.setAttribute("id", `attachment_${blob.filename}`);
          hiddenField.setAttribute("type", "hidden");
          hiddenField.setAttribute("value", blob.signed_id);
          hiddenField.dataset.directUploadTarget = 'cachedFile';
          hiddenField.dataset.directUploadName = file.name;
          hiddenField.name = this.inputTarget.name;
          this.element.appendChild(hiddenField);
          this.dispatch("uploaded", { detail: { file: file } });
          resolve(blob);
        }
      })
    });
  }

  #validateFiles(files) {
    return Array.from(this.inputTarget.files).filter((file) => this.#validateFile(file));
  }

  #validateFile(file) {
    const validFileSize = this.#validateFileSize(file);
    const validFileType = this.#validateFileType(file);

    return validFileSize && validFileType;
  }

  #validateFileType(file) {
    const acceptedTypes = this.inputTarget.accept.replace(/\s/g, "").split(",");
    const validations = acceptedTypes.map((type) => new RegExp(type.replace("*", ".*")));
    const isValid = validations.some((validation) => validation.test(file.type));

    if (isValid) {
      return true;
    } else {
      this.notices[file.name] ||= [];
      this.notices[file.name].push(`"${file.type}" is not an allowed type`);
      return false;
    }
  }

  #validateFileSize(file) {
    const maxFileSize = this.inputTarget.getAttribute("max-file-size");
    if (!maxFileSize) return true;
    const maxFileSizeNatural = maxFileSize.trim();

    const maxFileSizeBytes = fileSizeToBytes(maxFileSize);

    if (file.size <= maxFileSizeBytes) {
      return true;
    } else {
      this.notices[file.name] ||= [];
      this.notices[file.name].push(
          `${humanFileSize(file.size)} is larger than the ${maxFileSizeNatural} allowed`,
      );
      return false;
    }
  }

  #displayUploading(files) {
    this.downladingTarget.innerHTML = "";
    this.downladingTarget.classList.remove("hidden");
  }

  #hideUploading() {
    this.downladingTarget.classList.add("hidden");
    this.downladingTarget.innerHTML = "";
  }

  #updateUploadProgress({ file, progress }) {
    const li = this.downladingTarget.querySelector(`[data-file-name="${file.name}"]`);
    const innerHTML = `
      <div class="my-2">
        <p> Uploading <strong>${file.name}</strong> (${humanFileSize(file.size)}) - ${progress}% </p>
        <div class="overflow-hidden rounded-full bg-gray-200">
          <div class="h-2 bg-blue-600 rounded-full" style="width: ${progress}%"></div>
        </div>
      </div>`;
    if (li) {
      li.innerHTML = innerHTML;
    } else {
      const li = document.createElement("li");
      li.setAttribute("data-file-name", file.name);
      li.setAttribute("data-file-size", file.size);
      li.innerHTML = innerHTML;
      this.downladingTarget.appendChild(li);
    }
  }

  #emptyErrors() {
    this.noticeTarget.innerHTML = "";
    this.notices = {};
  }

  #displayErrors() {
    Object.entries(this.notices).forEach(([fileName, errors]) => {
      const li = document.createElement("li");
      li.innerHTML = `<strong>${fileName}</strong>`;
      this.noticeTarget.appendChild(li);

      const ul = document.createElement("ul");
      errors.forEach((error) => {
        const li = document.createElement("li");
        li.innerHTML = error;
        ul.appendChild(li);
      });
      this.noticeTarget.appendChild(ul);
    });
  }
}
